import type {
  AskZotAbout,
  MembershipProject,
  NeedsYouItem,
  ZotThread,
} from "@zworks/console-contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock3, LoaderCircle, MessageSquarePlus, Pencil, Search, Sparkles } from "lucide-react";
import * as React from "react";
import { Link, useMatches, useSearchParams } from "react-router";
import { ZotArtifactsPanel } from "@/components/zot-artifacts";
import { ZotWorkAttention } from "./zot-work-attention";
import { ZotChat } from "@/components/zot-chat";
import { ZotComposer } from "@/components/zot-composer";
import { ZotContextChip } from "@/components/ask-zot";
import { parseAskZotAbout } from "@/lib/ask-zot";
import { StatusBadge } from "@/components/bespoke/status-badge";
import { EmptyState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  ApiError,
  deleteZotThread,
  overviewQuery,
  renameZotThread,
  zotMessagesQuery,
  zotThreadsQuery,
} from "@/lib/api";
import { useCurrentProject } from "@/lib/project";
import { cn } from "@/lib/utils";

function prefillFor(item: NeedsYouItem): string | null {
  if (item.kind === "approval") return `Summarise the approval "${item.title}" and flag the risks.`;
  if (item.kind === "run")
    return `Why did the run "${item.title}" end up unknown, and what should I check?`;
  return null;
}

function NeedsAttentionBadge({ kind }: { kind: NeedsYouItem["kind"] }) {
  if (kind === "approval") return <StatusBadge kind="approval" status="pending" />;
  if (kind === "run") return <StatusBadge kind="run" status="unknown" />;
  return <StatusBadge kind="generic" status="pending" label="Invitation" />;
}

function HistoryPopover({
  threads,
  loading,
  failed,
  onRetry,
  onOpen,
  onRenamed,
  projectId,
  currentId,
}: {
  threads: ZotThread[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  onOpen: (threadId: string) => void;
  onRenamed: () => void;
  projectId: string | null;
  currentId: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [filter, setFilter] = React.useState("");
  const [renamingId, setRenamingId] = React.useState<string | null>(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [renameError, setRenameError] = React.useState<string | null>(null);
  const submittingRef = React.useRef(false);
  const visible = threads.filter((thread) =>
    filter.trim()
      ? (thread.title || "Untitled chat").toLowerCase().includes(filter.trim().toLowerCase())
      : true,
  );

  async function confirmRename(threadId: string) {
    if (submittingRef.current) return;
    if (!projectId || !renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    submittingRef.current = true;
    try {
      await renameZotThread(projectId, threadId, { title: renameValue.trim() });
      setRenamingId(null);
      setRenameError(null);
      onRenamed();
    } catch (cause) {
      setRenameError(cause instanceof ApiError ? cause.message : "The chat could not be renamed.");
    } finally {
      submittingRef.current = false;
    }
  }

  function cancelRename() {
    submittingRef.current = true;
    setRenamingId(null);
    setRenameError(null);
    queueMicrotask(() => {
      submittingRef.current = false;
    });
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Chat history"
        className="inline-flex h-9 items-center gap-1.5 rounded-md border px-2.5 text-sm hover:bg-accent"
      >
        <Clock3 className="size-4" aria-hidden />
        History
      </PopoverTrigger>
      <PopoverContent
        className="right-0 w-80 p-2"
        onKeyDown={(event) => {
          if (renamingId && event.key === "Escape") event.stopPropagation();
        }}
      >
        <label className="sr-only" htmlFor="zot-history-search">
          Search chats
        </label>
        <div className="relative p-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            id="zot-history-search"
            role="searchbox"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Search chats"
            className="pl-9"
          />
        </div>
        <div className="max-h-72 overflow-y-auto" aria-label="Chat history">
          {loading ? (
            <p role="status" className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <LoaderCircle className="size-4 animate-spin" aria-hidden />
              Loading chats…
            </p>
          ) : failed ? (
            <p role="alert" className="p-3 text-sm text-danger">
              Chats could not be loaded.{" "}
              <button type="button" className="underline" onClick={onRetry}>
                Try again
              </button>
            </p>
          ) : visible.length === 0 ? (
            <div className="p-2">
              <EmptyState
                title="No chats yet"
                description="Start a new chat. Past conversations appear here and stay private to you."
              />
            </div>
          ) : (
            visible.map((thread) => {
              const renaming = renamingId === thread.id;
              if (renaming) {
                return (
                  <form
                    key={thread.id}
                    className={cn(
                      "flex items-center gap-1 rounded-md p-1",
                      currentId === thread.id && "bg-muted",
                    )}
                    onSubmit={(event) => {
                      event.preventDefault();
                      void confirmRename(thread.id);
                    }}
                  >
                    <label className="sr-only" htmlFor={`rename-${thread.id}`}>
                      Chat title
                    </label>
                    <Input
                      id={`rename-${thread.id}`}
                      // biome-ignore lint/a11y/noAutofocus: returns focus to the active rename field
                      autoFocus
                      value={renameValue}
                      maxLength={120}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") cancelRename();
                      }}
                      className="min-w-0 flex-1"
                    />
                    <Button type="submit" size="sm">
                      Save
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={cancelRename}>
                      Cancel
                    </Button>
                  </form>
                );
              }
              return (
                <div
                  key={thread.id}
                  className={cn(
                    "group flex items-center gap-1 rounded-md hover:bg-accent",
                    currentId === thread.id && "bg-muted",
                  )}
                >
                  <button
                    type="button"
                    aria-current={currentId === thread.id ? "true" : undefined}
                    onClick={() => {
                      onOpen(thread.id);
                      setOpen(false);
                    }}
                    className="min-w-0 flex-1 rounded-md px-2 py-2 text-left"
                  >
                    <span className="block truncate text-sm font-medium">
                      {thread.title || "Untitled chat"}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {new Date(thread.createdAt).toLocaleDateString("en-GB", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mr-1 shrink-0 opacity-0 group-focus-within:opacity-100 group-hover:opacity-100"
                    aria-label={`Rename ${thread.title || "chat"}`}
                    onClick={() => {
                      submittingRef.current = false;
                      setRenameError(null);
                      setRenamingId(thread.id);
                      setRenameValue(thread.title);
                    }}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>
                </div>
              );
            })
          )}
          {renameError ? (
            <p role="alert" className="p-2 text-sm text-danger">
              {renameError}
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function ZotPage() {
  const { project } = useCurrentProject();
  const matches = useMatches();
  const pageContext = (matches.at(-1)?.handle as { title?: string } | undefined)?.title ?? "Zot";
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedThreadId = searchParams.get("thread");
  const prefill = searchParams.get("ask") ?? "";
  const submitPrefill = searchParams.get("send") === "1";
  const contextChip = searchParams.get("chip") ?? null;
  const askedObject = parseAskZotAbout(searchParams.get("about"));
  const queryClient = useQueryClient();
  const consumeAutomaticSend = React.useCallback(() => {
    setSearchParams(
      (current) => {
        if (current.get("send") !== "1") return current;
        const next = new URLSearchParams(current);
        next.delete("send");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const threads = useQuery({
    ...zotThreadsQuery(project?.id ?? ""),
    enabled: Boolean(project),
  });
  const overview = useQuery({
    ...overviewQuery(project?.id ?? ""),
    enabled: Boolean(project),
  });

  function selectThread(threadId: string | null, ask?: string, submitAsk = false) {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (threadId) next.set("thread", threadId);
      else next.delete("thread");
      if (ask !== undefined) {
        if (ask) {
          next.set("ask", ask);
          if (submitAsk) next.set("send", "1");
          else next.delete("send");
        } else {
          next.delete("ask");
          next.delete("send");
          next.delete("about");
          next.delete("chip");
        }
      }
      return next;
    });
  }

  async function handleThreadChange(threadId: string) {
    selectThread(threadId, "");
    if (project) await queryClient.invalidateQueries({ queryKey: ["projects", project.id, "zot"] });
  }

  async function startNewChat() {
    selectThread(null, "");
    if (project) await queryClient.invalidateQueries({ queryKey: ["projects", project.id, "zot"] });
  }

  async function confirmDelete(threadId: string) {
    if (!project) return;
    if (!window.confirm("Delete this chat? The messages are removed for you.")) return;
    try {
      await deleteZotThread(project.id, threadId);
      if (selectedThreadId === threadId) selectThread(null, "");
      await queryClient.invalidateQueries({ queryKey: ["projects", project.id, "zot"] });
    } catch {
      // Deletion is best effort from the popover; the threads query surfaces load errors.
    }
  }

  const needsYou = overview.data?.needsYou ?? [];
  const isConversation = selectedThreadId !== null || prefill !== "";

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col">
      <div className="flex items-center justify-between gap-2 py-2">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="flex size-7 items-center justify-center rounded-full bg-primary/10 text-accent-text">
            <Sparkles className="size-3.5" aria-hidden />
          </span>
          <span>{project ? project.name : "No project"} · sees this project only, read only</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => void startNewChat()}>
            <MessageSquarePlus className="mr-1 size-4" aria-hidden />
            New chat
          </Button>
          <HistoryPopover
            threads={threads.data ?? []}
            loading={threads.isPending}
            failed={threads.isError}
            onRetry={() => void threads.refetch()}
            onOpen={(threadId) => selectThread(threadId, "")}
            onRenamed={() => {
              if (project)
                void queryClient.invalidateQueries({ queryKey: ["projects", project.id, "zot"] });
            }}
            projectId={project?.id ?? null}
            currentId={selectedThreadId}
          />
        </div>
      </div>

      {isConversation ? (
        <ConversationView
          key={selectedThreadId ?? "new"}
          project={project}
          pageContext={pageContext}
          threadId={selectedThreadId}
          prefill={prefill}
          contextChip={contextChip}
          about={askedObject}
          submitPrefill={submitPrefill}
          onInitialDraftSubmit={consumeAutomaticSend}
          onThreadChange={(threadId) => void handleThreadChange(threadId)}
          onDelete={selectedThreadId ? () => void confirmDelete(selectedThreadId) : undefined}
        />
      ) : (
        <EmptyHome
          project={project}
          pageContext={pageContext}
          needsYou={needsYou}
          needsLoading={overview.isPending}
          onStart={(question, submit) => selectThread(null, question, submit)}
        />
      )}
    </div>
  );
}

function EmptyHome({
  project,
  pageContext,
  needsYou,
  needsLoading,
  onStart,
}: {
  project: MembershipProject | null;
  pageContext: string;
  needsYou: NeedsYouItem[];
  needsLoading: boolean;
  onStart: (question: string, submit?: boolean) => void;
}) {
  const [draft, setDraft] = React.useState("");

  function send() {
    if (!draft.trim()) return;
    onStart(draft.trim(), true);
  }

  return (
    <div className="flex flex-col items-center pb-8">
      <h1 className="mt-10 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
        What are we working on?
      </h1>
      <div className="relative mt-6 w-full">
        <div className="rounded-2xl border bg-card p-4 shadow-sm">
          <ZotComposer
            id="zot-home-message"
            value={draft}
            onChange={setDraft}
            onSubmit={send}
            disabled={!project}
            showShortcuts
          />
        </div>
      </div>
      <Link className="mt-2 text-sm text-muted-foreground underline underline-offset-4" to="/help">
        View Zot command guides and examples
      </Link>
      <p className="sr-only">
        Project: {project ? project.name : "None"} · Page: {pageContext || "Project"} · Role:{" "}
        {project ? project.role : "None"}
      </p>

      <div className="mt-8 w-full">
        <h2 className="text-sm font-semibold text-muted-foreground">Needs your attention</h2>
        {!project ? (
          <EmptyState
            title="No projects yet"
            description="Create a project to start working with Zot."
          />
        ) : needsLoading ? (
          <p role="status" className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
            <LoaderCircle className="size-4 animate-spin" aria-hidden />
            Loading what needs you…
          </p>
        ) : needsYou.length === 0 ? (
          <div className="mt-3">
            <EmptyState
              title="Nothing needs you"
              description="Approvals waiting for you and runs with an unknown outcome appear here."
            />
          </div>
        ) : (
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {needsYou.map((item) => {
              const question = prefillFor(item);
              return (
                <li
                  key={`${item.kind}-${item.id}`}
                  className="flex flex-col gap-2 rounded-xl border bg-card p-3"
                >
                  <NeedsAttentionBadge kind={item.kind} />
                  <p className="text-sm font-medium">{item.title}</p>
                  <div className="mt-auto flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <Link to={item.href}>{item.kind === "approval" ? "Review" : "Open"}</Link>
                    </Button>
                    {question ? (
                      <Button variant="ghost" size="sm" onClick={() => onStart(question)}>
                        Ask Zot
                      </Button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {project ? <ZotWorkAttention projectId={project.id} /> : null}
      </div>
    </div>
  );
}

function ConversationView({
  project,
  pageContext,
  threadId,
  prefill,
  submitPrefill,
  contextChip,
  about,
  onInitialDraftSubmit,
  onThreadChange,
  onDelete,
}: {
  project: MembershipProject | null;
  pageContext: string;
  threadId: string | null;
  prefill: string;
  submitPrefill: boolean;
  contextChip: string | null;
  about: AskZotAbout | null;
  onInitialDraftSubmit: () => void;
  onThreadChange: (threadId: string) => void;
  onDelete?: () => void;
}) {
  const history = useQuery({
    ...zotMessagesQuery(project?.id ?? "", threadId ?? ""),
    enabled: Boolean(project && threadId),
  });
  const [artifactsOpen, setArtifactsOpen] = React.useState(false);
  const queryClient = useQueryClient();
  if (threadId && history.isPending) {
    return (
      <p role="status" className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
        <LoaderCircle className="size-4 animate-spin" aria-hidden />
        Loading chat…
      </p>
    );
  }
  if (threadId && history.isError) {
    return (
      <p role="alert" className="p-4 text-sm text-danger">
        This chat could not be loaded.{" "}
        <button type="button" className="underline" onClick={() => void history.refetch()}>
          Try again
        </button>
      </p>
    );
  }
  return (
    <div className="flex min-h-[60svh] flex-col">
      <div className="flex items-center justify-between gap-2 py-1">
        <ZotContextChip chip={contextChip} />
        {threadId && onDelete ? (
          <Button variant="ghost" size="sm" onClick={onDelete}>
            Delete this chat
          </Button>
        ) : null}
      </div>
      <div className="flex min-h-0 flex-1 items-start gap-4">
        <ZotChat
          key={`${threadId ?? "new"}:${prefill}`}
          project={project}
          pageContext={pageContext}
          className="min-h-[60svh] min-w-0 flex-1"
          threadId={threadId}
          onThreadChange={onThreadChange}
          autoTitle
          submitInitialDraft={submitPrefill}
          onInitialDraftSubmit={onInitialDraftSubmit}
          initialDraft={prefill}
          about={about}
          initialMessages={history.data?.messages ?? []}
          hideHeader
        />
        <div className={artifactsOpen ? "w-80 shrink-0" : "shrink-0"}>
          <ZotArtifactsPanel
            project={project}
            threadId={threadId}
            open={artifactsOpen}
            onToggle={() => setArtifactsOpen((current) => !current)}
            onProposal={() => {
              if (project && threadId) {
                void queryClient.invalidateQueries({
                  queryKey: ["projects", project.id, "zot", threadId],
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
}
